import { AuditEntity, Column, Display, FormField, prop, required, Table } from "@messaia/cdk";
import { BlacklistedCustomerDataField } from "../enums/customer-data-blacklist-data-field";

@Table({ endpoint: 'customerDataBlacklist', includes: ['Client'] })
export class CustomerDataBlacklist extends AuditEntity {
    /**
     * @property The ID of the associated client.
     * @description This property links the blacklist entry to a specific client.
     * @type {number}
     */
    @required()
    @Column({
        index: 1,
        name: 'client.displayName',
        endpoint: 'client?enabled=true&projection=id,name',
        maxWidth: 250,
        showGtSm: true
    })
    @FormField({
        row: 1,
        endpoint: 'client?enabled=true&projection=id,name'
    })
    @Display($localize`:@@client:Client`)
    public clientId: number;

    /**
     * @property The customer data fields to be blacklisted.
     * @description Specifies which fields (e.g., Name, Address, IBAN, Email) are blacklisted.
     * @type {BlacklistedCustomerDataField[]}
     */
    @required()
    @Column({
        enumType: BlacklistedCustomerDataField,
        showGtMd: true,
        multiple: true
    })
    @FormField({
        row: 10,
        enumType: BlacklistedCustomerDataField,
        multiple: true
    })
    @Display($localize`:@@dataFields:Data fields`)
    public dataFields: BlacklistedCustomerDataField[];

    /**
     * @property The customer's first name.
     * @description Used when the customer's name is blacklisted.
     * @type {string}
     */
    @required({ conditionalExpression: (x: CustomerDataBlacklist) => x.dataFields?.includes(BlacklistedCustomerDataField.Name) })
    @Column({
        index: 1,
        showGtXl: true
    })
    @FormField({
        row: 20,
        flex: 50,
        hide: (x: CustomerDataBlacklist) => !x.dataFields?.includes(BlacklistedCustomerDataField.Name)
    })
    @Display($localize`:@@firstName:First name`)
    public firstName: string;

    /**
     * @property The customer's last name.
     * @description Used when the customer's name is blacklisted.
     * @type {string}
     */
    @required({ conditionalExpression: (x: CustomerDataBlacklist) => x.dataFields?.includes(BlacklistedCustomerDataField.Name) })
    @Column({
        index: 1,
        showGtXl: true
    })
    @FormField({
        row: 20,
        flex: 50,
        hide: (x: CustomerDataBlacklist) => !x.dataFields?.includes(BlacklistedCustomerDataField.Name)
    })
    @Display($localize`:@@lastName:Last name`)
    public lastName: string;

    /**
     * @property The first address line.
     * @description Used when the customer's address is blacklisted.
     * @type {string}
     */
    @required({ conditionalExpression: (x: CustomerDataBlacklist) => x.dataFields?.includes(BlacklistedCustomerDataField.Address) })
    @Column({
        index: 1,
        showGtXl: true
    })
    @FormField({
        row: 30,
        flex: 60,
        hide: (x: CustomerDataBlacklist) => !x.dataFields?.includes(BlacklistedCustomerDataField.Address)
    })
    @Display($localize`:@@address1:Address 1`)
    public address1: string;

    /**
     * @property The second address line.
     * @description Used when the customer's address is blacklisted.
     * @type {string}
     */
    @required({ conditionalExpression: (x: CustomerDataBlacklist) => x.dataFields?.includes(BlacklistedCustomerDataField.Address) })
    @Column({
        index: 2,
        width: 60,
        showGtXl: true
    })
    @FormField({
        row: 30,
        flex: 20,
        hide: (x: CustomerDataBlacklist) => !x.dataFields?.includes(BlacklistedCustomerDataField.Address)
    })
    @Display($localize`:@@address2:Address 2`)
    public address2: string;

    /**
     * @property The third address line.
     * @description An optional additional address field for extended address details.
     * @type {string}
     */
    @prop()
    @FormField({
        row: 30,
        flex: 20,
        hide: (x: CustomerDataBlacklist) => !x.dataFields?.includes(BlacklistedCustomerDataField.Address)
    })
    @Display($localize`:@@address3:Address 3`)
    public address3: string;

    /**
     * @property The postal code of the address.
     * @description Used when the customer's address is blacklisted.
     * @type {string}
     */
    @required({ conditionalExpression: (x: CustomerDataBlacklist) => x.dataFields?.includes(BlacklistedCustomerDataField.Address) })
    @Column({
        index: 3,
        width: 80,
        showGtXl: true
    })
    @FormField({
        row: 40,
        flex: 25,
        hide: (x: CustomerDataBlacklist) => !x.dataFields?.includes(BlacklistedCustomerDataField.Address)
    })
    @Display($localize`:@@postalCode:Postal code`)
    public postalCode: string;

    /**
     * @property The city of the address.
     * @description Used when the customer's address is blacklisted.
     * @type {string}
     */
    @required({ conditionalExpression: (x: CustomerDataBlacklist) => x.dataFields?.includes(BlacklistedCustomerDataField.Address) })
    @Column({
        index: 4,
        width: 160,
        showGtXl: true
    })
    @FormField({
        row: 40,
        flex: 75,
        hide: (x: CustomerDataBlacklist) => !x.dataFields?.includes(BlacklistedCustomerDataField.Address)
    })
    @Display($localize`:@@city:City`)
    public city: string;

    /**
     * @property The customer's IBAN.
     * @description Used when the customer's IBAN is blacklisted.
     * @type {string}
     */
    @required({ conditionalExpression: (x: CustomerDataBlacklist) => x.dataFields?.includes(BlacklistedCustomerDataField.IBAN) })
    @Column({
        index: 5,
        showGtXl: true
    })
    @FormField({
        row: 50,
        hide: (x: CustomerDataBlacklist) => !x.dataFields?.includes(BlacklistedCustomerDataField.IBAN)
    })
    @Display($localize`:@@iban:IBAN`)
    public iban: string;

    /**
     * @property The customer's email address.
     * @description Used when the customer's email address is blacklisted.
     * @type {string}
     */
    @required({ conditionalExpression: (x: CustomerDataBlacklist) => x.dataFields?.includes(BlacklistedCustomerDataField.Email) })
    @Column({
        index: 6,
        showGtXl: true
    })
    @FormField({
        row: 60,
        hide: (x: CustomerDataBlacklist) => !x.dataFields?.includes(BlacklistedCustomerDataField.Email)
    })
    @Display($localize`:@@email:Email`)
    public email: string;

    /**
     * @property The customer's IBAN.
     * @description Used when the customer's IBAN is blacklisted.
     * @type {string}
     */
    @required({ conditionalExpression: (x: CustomerDataBlacklist) => x.dataFields?.includes(BlacklistedCustomerDataField.Number) })
    @Column({
        index: 7,
        showGtXl: true
    })
    @FormField({
        row: 70,
        hide: (x: CustomerDataBlacklist) => !x.dataFields?.includes(BlacklistedCustomerDataField.Number)
    })
    @Display($localize`:@@customerNumber:Customer number`)
    public number: number;

    /**
     * @property The enabled status of the blacklist entry.
     * @description Determines whether this blacklist record is currently active.
     * @type {boolean}
     */
    @prop()
    @FormField({ row: 100 })
    @Column({
        event: 'toggle'
    })
    @Display($localize`:@@enabled:Enabled`)
    public enabled: boolean;

    /**
     * @constructor Initializes a new instance of CustomerDataBlacklist.
     * @param init Optional object to initialize property values.
     */
    public constructor(init?: Partial<CustomerDataBlacklist>) {
        super(init);
        Object.assign(this, init);
    }
}
